import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { createUserInFirestore } from '@/utils/firebase/user';
import {
  Users,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

type AuthMode = 'signin' | 'signup';

const AffiliateSignIn: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const validateForm = (): boolean => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return false;
    }

    if (mode === 'signup') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const checkAffiliateStatus = async (userEmail: string): Promise<{ exists: boolean; approved: boolean }> => {
    try {
      const getAffiliateStats = httpsCallable(functions, 'getAffiliateStats');
      const result = await getAffiliateStats({ email: userEmail });
      const data = result.data as { affiliate?: { status: string } };

      if (data.affiliate) {
        return {
          exists: true,
          approved: data.affiliate.status === 'active'
        };
      }
      return { exists: false, approved: false };
    } catch {
      // If function throws, affiliate doesn't exist
      return { exists: false, approved: false };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (mode === 'signup') {
        // Check if they have an approved affiliate application first
        const affiliateStatus = await checkAffiliateStatus(email);

        if (!affiliateStatus.exists) {
          setError('No affiliate application found for this email. Please apply first.');
          setIsSubmitting(false);
          return;
        }

        if (!affiliateStatus.approved) {
          setError('Your affiliate application is still pending review. Please wait for approval.');
          setIsSubmitting(false);
          return;
        }

        // Create Firebase Auth account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Create user document in Firestore (required for the app to work)
        await createUserInFirestore(
          userCredential.user.uid,
          email,
          {
            marketingEmailConsent: false,
            transactionalEmailConsent: true,
            termsAcceptedAt: new Date().toISOString()
          },
          {
            name: '', // Affiliate can update their name later
            provider: 'affiliate'
          }
        );

        navigate('/affiliate/dashboard', { replace: true });
      } else {
        // Sign in existing account
        await signInWithEmailAndPassword(auth, email, password);

        // Verify they're an affiliate
        const affiliateStatus = await checkAffiliateStatus(email);
        if (!affiliateStatus.exists) {
          setError('No affiliate account found for this email.');
          await auth.signOut();
          setIsSubmitting(false);
          return;
        }

        navigate('/affiliate/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      console.error('Auth error:', err);
      const errorCode = (err as { code?: string })?.code;

      switch (errorCode) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists. Try signing in instead.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use at least 8 characters.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Invalid email or password.');
          break;
        default:
          setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{mode === 'signin' ? 'Affiliate Sign In' : 'Create Affiliate Account'} - SWTPA</title>
      </Helmet>

      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 mb-4">
              <Users className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              Affiliate Portal
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
              {mode === 'signin'
                ? 'Sign in to access your dashboard'
                : 'Create your affiliate account'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-ledge-lg border border-neutral-200 dark:border-neutral-700 p-8">
            {/* Mode Toggle */}
            <div className="flex rounded-lg bg-neutral-100 dark:bg-neutral-700 p-1 mb-6">
              <button
                type="button"
                onClick={() => { setMode('signin'); setError(''); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  mode === 'signin'
                    ? 'bg-white dark:bg-neutral-600 text-neutral-900 dark:text-white shadow-ledge-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => { setMode('signup'); setError(''); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  mode === 'signup'
                    ? 'bg-white dark:bg-neutral-600 text-neutral-900 dark:text-white shadow-ledge-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
                }`}
              >
                Create Account
              </button>
            </div>

            {/* Info Banner for Signup */}
            {mode === 'signup' && (
              <div className="mb-6 p-4 rounded-lg bg-primary-50 dark:bg-neutral-700 border border-primary-200 dark:border-neutral-600">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary-900 dark:text-white">
                      Already approved?
                    </p>
                    <p className="text-sm text-primary-700 dark:text-neutral-300">
                      Use the same email from your affiliate application to create your account.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {mode === 'signup' && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 text-lg"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {mode === 'signin' ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  <>
                    {mode === 'signin' ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Footer Links */}
            <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700 text-center">
              {mode === 'signin' ? (
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Don't have an account?{' '}
                  <Link to="/affiliate/signup" className="text-primary-600 hover:underline font-medium">
                    Apply to join
                  </Link>
                </p>
              ) : (
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Haven't applied yet?{' '}
                  <Link to="/affiliate/signup" className="text-primary-600 hover:underline font-medium">
                    Apply first
                  </Link>
                </p>
              )}
            </div>
          </div>

          {/* Back to main site */}
          <p className="mt-6 text-center text-sm text-neutral-500 dark:text-neutral-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              ← Back to main site
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default AffiliateSignIn;
